import { useCallback, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { NewProjectDialog } from "@/pages/projekte/NewProjectDialog";
import type { CreatedOrder } from "@/lib/projects";
import {
  NewProjectContext,
  type NewProjectContextValue,
  type NewProjectCreatedHandler,
  type NewProjectFormPrefill,
  type NewProjectOrdnerPrefill,
} from "@/pages/projekte/newProjectContextValue";

/**
 * Globaler Kontext für die "Neues Projekt"-Anlage.
 *
 * Der Provider rendert den `NewProjectDialog` einmalig im Layout und stellt
 * über `useNewProject()` eine `open()`-Funktion bereit, die von einem beliebigen
 * Button auf einer beliebigen Seite genutzt werden kann.
 */

export function NewProjectProvider({ children }: { children: ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [ordnerPrefill, setOrdnerPrefill] = useState<NewProjectOrdnerPrefill | null>(null);
  const [formPrefill, setFormPrefill] = useState<NewProjectFormPrefill | null>(null);
  const [skipAskCommissionAfterCreate, setSkipAskCommissionAfterCreate] = useState(false);
  const oneShotRef = useRef<NewProjectCreatedHandler | null>(null);
  const listenersRef = useRef<Set<NewProjectCreatedHandler>>(new Set());

  const close = useCallback(() => {
    setIsOpen(false);
    oneShotRef.current = null;
    setOrdnerPrefill(null);
    setFormPrefill(null);
    setSkipAskCommissionAfterCreate(false);
  }, []);

  const open = useCallback(
    (options?: {
      onCreated?: NewProjectCreatedHandler;
      ordnerPrefill?: NewProjectOrdnerPrefill;
      formPrefill?: NewProjectFormPrefill;
      skipAskCommissionAfterCreate?: boolean;
    }) => {
      oneShotRef.current = options?.onCreated ?? null;
      setOrdnerPrefill(options?.ordnerPrefill ?? null);
      setFormPrefill(options?.formPrefill ?? null);
      setSkipAskCommissionAfterCreate(Boolean(options?.skipAskCommissionAfterCreate));
      setIsOpen(true);
    },
    [],
  );

  const subscribe = useCallback((listener: NewProjectCreatedHandler) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const handleCreated = useCallback((order: CreatedOrder) => {
    if (oneShotRef.current) {
      try {
        oneShotRef.current(order);
      } catch (err) {
        console.warn("[NewProjectProvider] one-shot listener failed", err);
      }
      oneShotRef.current = null;
    }
    listenersRef.current.forEach((listener) => {
      try {
        listener(order);
      } catch (err) {
        console.warn("[NewProjectProvider] listener failed", err);
      }
    });
  }, []);

  const value = useMemo<NewProjectContextValue>(
    () => ({ open, close, isOpen, subscribe }),
    [open, close, isOpen, subscribe],
  );

  return (
    <NewProjectContext.Provider value={value}>
      {children}
      <NewProjectDialog
        open={isOpen}
        onClose={close}
        onCreated={handleCreated}
        initialOrdnerPrefill={ordnerPrefill}
        initialFormPrefill={formPrefill}
        skipAskCommissionAfterCreate={skipAskCommissionAfterCreate}
      />
    </NewProjectContext.Provider>
  );
}
